import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import type { Environment } from '~/engine/data/Environment';
import { multiSelectedRoiNumbersState } from '~/state/atoms/structureSet/roiAtoms';
import { roiPreview3DOpenState } from '~/state/atoms/viewerAtoms';
import { previewDrawableRoiNumbers } from './previewRoiNumbers';

/** What the 3D preview draws: the selection in the Environment holding it. */
export interface RoiPreviewSubject {
    /** The Environment holding the selected ROIs. */
    readonly environment: Environment | undefined;
    /**
     * The structure set the selection belongs to; the scope of the live
     * refresh. Undefined while no set is active.
     */
    readonly structureSetUid: string | undefined;
    /** The active ROI, or -1 when nothing is selected. */
    readonly selectedRoiNumber: number;
    /** Bulk-selected ROI numbers; empty, or two or more. */
    readonly multiSelectedRoiNumbers: ReadonlySet<number>;
}

/**
 * The ROIs of the selection the preview could draw in an Environment, in
 * ascending ROI-number order: the selection, or the bulk selection, less
 * the ROIs that are not loaded there and the point ROIs, which have no
 * surface to march. Empty without an Environment. Cheap and purely derived.
 */
export function useRoiPreviewDrawableRoiNumbers(
    environment: Environment | undefined,
    selectedRoiNumber: number,
    multiSelectedRoiNumbers: ReadonlySet<number>
): readonly number[] {
    return useMemo(
        () =>
            previewDrawableRoiNumbers({
                selectedRoiNumber,
                multiSelectedRoiNumbers,
                isDrawable: (roiNumber) => {
                    const roi = environment?.getRoi(roiNumber);
                    return roi !== undefined && !roi.isPointRoi;
                },
            }),
        [environment, selectedRoiNumber, multiSelectedRoiNumbers]
    );
}

/**
 * What the 3D preview shows, and whether it is shown at all: the global
 * selection in the edit target's Environment, scoped to the edit target's
 * structure set, while the preview toggle is on and the selection holds
 * something to draw. `undefined` otherwise, so a host that gets nothing
 * offers nothing. The Environment is the one that draws the active set,
 * and none while that set's dataset is not resident.
 */
export function useRoiPreviewSubject(): RoiPreviewSubject | undefined {
    const isPreviewOpen = useRecoilValue(roiPreview3DOpenState);
    const { environment, structureSet, roiNumber } = useActiveEditTarget();
    const multiSelectedRoiNumbers = useRecoilValue(
        multiSelectedRoiNumbersState
    );
    const drawableRoiNumbers = useRoiPreviewDrawableRoiNumbers(
        environment,
        roiNumber,
        multiSelectedRoiNumbers
    );
    const structureSetUid = structureSet?.dicomUid;
    return useMemo(() => {
        if (!isPreviewOpen || drawableRoiNumbers.length === 0) {
            return undefined;
        }
        return {
            environment,
            structureSetUid,
            selectedRoiNumber: roiNumber,
            multiSelectedRoiNumbers,
        };
    }, [
        isPreviewOpen,
        drawableRoiNumbers,
        environment,
        structureSetUid,
        roiNumber,
        multiSelectedRoiNumbers,
    ]);
}
